import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { ArrowLeft, Globe, Phone, MessageCircle, Info, Upload, X, FileText } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

const SERVICE_TYPES = {
  aannemer: { nl: 'Aannemer', en: 'General Contractor' },
  timmerwerk: { nl: 'Timmerwerk', en: 'Carpentry' },
  stucwerk: { nl: 'Stucwerk', en: 'Plastering' },
  schilderwerk: { nl: 'Schilderwerk/Onderhouds Abonnement', en: 'Painting/Maintenance Subscription' },
  badkamer: { nl: 'Badkamer Specialist', en: 'Bathroom Specialist' },
  renovatie: { nl: 'Renovatie', en: 'Renovation' }
};

const NewWorkRequest = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    service_type: '',
    title: '',
    description: '',
    address: '',
    preferred_start_date: '',
  });
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(false);

  const toggleLanguage = () => {
    const newLang = i18n.language === 'nl' ? 'en' : 'nl';
    i18n.changeLanguage(newLang);
  };

  const handleFileChange = (e) => {
    const selectedFiles = Array.from(e.target.files);
    setFiles(prev => [...prev, ...selectedFiles]);
  };

  const removeFile = (index) => {
    setFiles(prev => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    const token = localStorage.getItem('clientToken');
    if (!token) {
      navigate('/client');
      return;
    }

    try {
      const formDataToSend = new FormData();
      formDataToSend.append('service_type', formData.service_type);
      formDataToSend.append('title', formData.title);
      formDataToSend.append('description', formData.description);
      formDataToSend.append('address', formData.address);
      if (formData.preferred_start_date) {
        formDataToSend.append('preferred_start_date', formData.preferred_start_date);
      }
      
      files.forEach((file) => {
        formDataToSend.append('files', file);
      });

      await axios.post(`${BACKEND_URL}/api/client/work-requests`, formDataToSend, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data',
        },
      });
      
      toast.success(t('requestSubmitted'));
      navigate('/client/dashboard');
    } catch (error) {
      toast.error(t('error'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#0a2a2a] via-[#0d3d3d] to-[#1a5656] p-4 md:p-8">
      <div className="max-w-3xl mx-auto">
        <div className="flex justify-between items-center mb-6">
          <Button
            variant="ghost"
            onClick={() => navigate('/client/dashboard')}
            className="text-white hover:bg-white/10"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            {t('dashboard')}
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={toggleLanguage}
            className="text-white hover:bg-white/10"
          >
            <Globe className="w-4 h-4 mr-2" />
            {i18n.language === 'nl' ? 'EN' : 'NL'}
          </Button>
        </div>

        {/* Informações de Contato */}
        <Card className="bg-[#0d3d3d]/90 border-white/20 backdrop-blur mb-6">
          <CardHeader>
            <CardTitle className="text-2xl font-bold text-white flex items-center gap-2">
              <Info className="w-6 h-6" />
              {i18n.language === 'nl' ? 'Contact Informatie' : 'Contact Information'}
            </CardTitle>
            <CardDescription className="text-gray-300 text-lg">
              {i18n.language === 'nl' 
                ? 'We nemen binnen 24 uur contact met u op!' 
                : 'We will contact you within 24 hours!'}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="flex items-center gap-3 text-white">
              <Phone className="w-5 h-5 text-[#3da5a5]" />
              <a href="tel:+31685071009" className="text-lg hover:underline">
                +31 6 8507 1009
              </a>
            </div>
            <div className="flex items-center gap-3">
              <MessageCircle className="w-5 h-5 text-green-400" />
              <a
                href="https://wa.me/31685071009"
                target="_blank"
                rel="noopener noreferrer"
                className="text-lg text-green-400 hover:underline"
              >
                WhatsApp
              </a>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-[#0d3d3d]/90 border-white/20 backdrop-blur">
          <CardHeader>
            <CardTitle className="text-3xl font-bold text-white">
              {t('newRequest')}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <Label className="text-white mb-2 block text-lg">
                  {i18n.language === 'nl' ? 'Type Dienst' : 'Service Type'} *
                </Label>
                <Select value={formData.service_type} onValueChange={(value) => setFormData({ ...formData, service_type: value })}>
                  <SelectTrigger className="bg-white/10 border-white/30 text-white text-lg py-6">
                    <SelectValue placeholder={i18n.language === 'nl' ? 'Selecteer een dienst' : 'Select a service'} />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.entries(SERVICE_TYPES).map(([key, value]) => (
                      <SelectItem key={key} value={key}>
                        {value[i18n.language]}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label className="text-white mb-2 block text-lg">{t('requestTitle')} *</Label>
                <Input
                  type="text"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  required
                  className="bg-white/10 border-white/30 text-white text-lg py-6"
                  placeholder={i18n.language === 'nl' ? 'Bijv. Badkamer renovatie' : 'E.g. Bathroom renovation'}
                />
              </div>

              <div>
                <Label className="text-white mb-2 block text-lg">{t('description')} *</Label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  required
                  rows="6"
                  className="w-full bg-white/10 border-white/30 text-white rounded-md p-4 text-lg"
                  placeholder={i18n.language === 'nl' 
                    ? 'Beschrijf het werk dat u wilt laten uitvoeren...'
                    : 'Describe the work you need done...'}
                />
              </div>

              <div>
                <Label className="text-white mb-2 block text-lg">{t('workAddress')} *</Label>
                <Input
                  type="text"
                  value={formData.address}
                  onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                  required
                  className="bg-white/10 border-white/30 text-white text-lg py-6"
                  placeholder="Straat 123, Amsterdam"
                />
              </div>

              <div>
                <Label className="text-white mb-2 block text-lg">{t('preferredStartDate')}</Label>
                <Input
                  type="date"
                  value={formData.preferred_start_date}
                  onChange={(e) => setFormData({ ...formData, preferred_start_date: e.target.value })}
                  className="bg-white/10 border-white/30 text-white text-lg py-6"
                />
              </div>

              {/* Upload de Fotos e Documentos */}
              <div>
                <Label className="text-white mb-2 block text-lg">
                  {i18n.language === 'nl' ? 'Bestanden toevoegen (optioneel)' : 'Add Files (optional)'}
                </Label>
                <div className="space-y-3">
                  <label className="flex items-center justify-center w-full bg-white/10 border-2 border-dashed border-white/30 rounded-lg p-6 cursor-pointer hover:bg-white/20 transition">
                    <div className="text-center">
                      <Upload className="w-8 h-8 text-white mx-auto mb-2" />
                      <p className="text-white text-lg">
                        {i18n.language === 'nl' ? 'Klik om bestanden te uploaden' : 'Click to upload files'}
                      </p>
                      <p className="text-white/60 text-sm mt-1">
                        {i18n.language === 'nl' ? 'Afbeeldingen, PDF, Word, Excel' : 'Images, PDF, Word, Excel'}
                      </p>
                    </div>
                    <input
                      type="file"
                      accept="image/*,.pdf,.doc,.docx,.xls,.xlsx"
                      multiple
                      onChange={handleFileChange}
                      className="hidden"
                    />
                  </label>
                  
                  {files.length > 0 && (
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                      {files.map((file, index) => {
                        const isImage = file.type.startsWith('image/');
                        const isPDF = file.type === 'application/pdf';
                        
                        return (
                          <div key={index} className="relative bg-white/10 rounded-lg p-3">
                            {isImage ? (
                              <img
                                src={URL.createObjectURL(file)}
                                alt={file.name}
                                className="w-full h-32 object-cover rounded"
                              />
                            ) : (
                              <div className="flex flex-col items-center justify-center h-32 bg-blue-900/30 rounded">
                                <FileText className="w-12 h-12 text-blue-300 mb-2" />
                                <p className="text-xs text-white truncate w-full px-2 text-center">
                                  {isPDF ? 'PDF' : file.name.split('.').pop().toUpperCase()}
                                </p>
                              </div>
                            )}
                            <button
                              type="button"
                              onClick={() => removeFile(index)}
                              className="absolute top-1 right-1 bg-red-500 text-white rounded-full p-1 hover:bg-red-600"
                            >
                              <X className="w-4 h-4" />
                            </button>
                            <p className="text-white text-xs mt-2 truncate">{file.name}</p>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>

              <Button
                type="submit"
                disabled={loading}
                className="w-full bg-[#3da5a5] hover:bg-[#2d8585] text-white py-8 text-xl font-semibold"
              >
                {loading ? '...' : t('submit')}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default NewWorkRequest;
